(() => {
  if (customElements.get('arista-instagram-feed')) return;

  const clamp = (value, minimum, maximum) => Math.min(Math.max(value, minimum), maximum);

  const normaliseText = (value, fallback = '') => (typeof value === 'string' ? value.trim() : fallback);

  const safeMediaUrl = (value) => {
    if (!value) return '';

    try {
      const url = new URL(value, window.location.origin);
      const hostname = url.hostname.toLowerCase();
      const allowed =
        url.protocol === 'https:' &&
        (hostname === window.location.hostname ||
          hostname === 'cdn.shopify.com' ||
          hostname.endsWith('.myshopify.com') ||
          hostname.endsWith('.shopifycdn.com') ||
          hostname.endsWith('.cdninstagram.com') ||
          hostname.endsWith('.fbcdn.net'));

      return allowed ? url.href : '';
    } catch (_error) {
      return '';
    }
  };

  class AristaInstagramFeed extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.grid = this.querySelector('[data-arista-instagram-grid]');
      this.status = this.querySelector('[data-arista-instagram-status]');
      this.modal = document.getElementById(this.dataset.modalId);
      this.modalImage = this.modal?.querySelector('[data-arista-instagram-modal-image]');
      this.modalVideo = this.modal?.querySelector('[data-arista-instagram-modal-video]');
      this.modalFallback = this.modal?.querySelector('[data-arista-instagram-modal-fallback]');
      this.modalSource = this.modal?.querySelector('[data-arista-instagram-modal-source]');
      this.modalTitle = this.modal?.querySelector('[data-arista-instagram-modal-title]');
      this.modalCaption = this.modal?.querySelector('[data-arista-instagram-modal-caption]');
      this.modalCta = this.modal?.querySelector('[data-arista-instagram-modal-cta]');
      this.shareButton = this.modal?.querySelector('[data-arista-instagram-share]');
      this.shareStatus = this.modal?.querySelector('[data-arista-instagram-share-status]');

      if (!this.grid || !this.modal) {
        this.hideFeed();
        return;
      }

      this.limit = clamp(Number.parseInt(this.dataset.limit, 10) || 8, 1, 48);
      this.previewMilliseconds = clamp(Number.parseInt(this.dataset.previewMs, 10) || 1000, 800, 1600);
      this.timeoutMilliseconds = clamp(Number.parseInt(this.dataset.timeoutMs, 10) || 6000, 2000, 10000);
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.saveData = Boolean(navigator.connection?.saveData);
      this.previewEntries = new Map();
      this.modalOpen = false;

      this.handleSectionClick = this.handleSectionClick.bind(this);
      this.handleModalClick = this.handleModalClick.bind(this);
      this.handleModalClosed = this.handleModalClosed.bind(this);
      this.handleModalImageError = this.handleModalImageError.bind(this);
      this.addEventListener('click', this.handleSectionClick);
      this.modal.addEventListener('click', this.handleModalClick);
      this.modalImage?.addEventListener('error', this.handleModalImageError);
      document.body.addEventListener('modalClosed', this.handleModalClosed);

      this.registerPreviewMedia(this.querySelectorAll('[data-arista-instagram-preview]'));
      this.loadFeed();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      window.clearTimeout(this.fetchTimeout);
      if (this.resumePreviewFrame) window.cancelAnimationFrame(this.resumePreviewFrame);
      this.previewObserver?.disconnect();
      this.stopActivePreview();
      this.removeEventListener('click', this.handleSectionClick);
      this.modal?.removeEventListener('click', this.handleModalClick);
      this.modalImage?.removeEventListener('error', this.handleModalImageError);
      document.body.removeEventListener('modalClosed', this.handleModalClosed);
      this.modal?.remove();
    }

    async loadFeed() {
      this.abortController = new AbortController();
      this.fetchTimeout = window.setTimeout(() => this.abortController.abort(), this.timeoutMilliseconds);

      try {
        const response = await fetch(this.buildFeedUrl(), {
          method: 'GET',
          mode: 'cors',
          credentials: 'omit',
          cache: 'no-store',
          headers: { Accept: 'application/json' },
          signal: this.abortController.signal,
        });

        if (!response.ok) throw new Error('Feed unavailable');

        const payload = await response.json();
        const posts = this.normalisePosts(payload);
        if (!posts.length) throw new Error('Feed empty');

        this.renderPosts(posts);
        this.grid.hidden = false;
        this.grid.setAttribute('aria-busy', 'false');
        this.hidden = false;
        this.setStatus(`${posts.length} Arista stories loaded.`);
      } catch (_error) {
        this.hideFeed();
      } finally {
        window.clearTimeout(this.fetchTimeout);
      }
    }

    buildFeedUrl() {
      const endpoint = new URL(this.dataset.feedEndpoint);
      endpoint.searchParams.set('limit', String(this.limit));
      endpoint.searchParams.set('account', this.dataset.account || 'arista-kids-jewels.myshopify.com');
      endpoint.searchParams.set('fu', '0');
      endpoint.searchParams.set('fid', this.dataset.feedId || '0');
      endpoint.searchParams.set('hash', this.dataset.publicHash || '');
      endpoint.searchParams.set('locale', this.dataset.locale || 'en');
      endpoint.searchParams.set('handle', '');
      return endpoint.href;
    }

    normalisePosts(payload) {
      if (!payload || Number.parseInt(payload.meta?.code, 10) !== 200 || !Array.isArray(payload.data)) return [];

      const sourceLabel = normaliseText(payload.meta?.username, 'aristajewels.in').replace(/^@/, '');

      return payload.data
        .slice(0, this.limit)
        .map((post) => {
          const image = safeMediaUrl(post?.images?.standard_resolution?.url);
          if (!image) return null;

          const video = post?.type === 'video' ? safeMediaUrl(post?.videos?.standard_resolution?.url) : '';
          const caption = normaliseText(post?.caption?.text);
          const alt = normaliseText(post?.alt_text, caption || 'Arista jewellery story');

          return {
            id: normaliseText(String(post?.id || '')),
            image,
            video,
            caption,
            alt,
            sourceLabel: `@${sourceLabel}`,
          };
        })
        .filter(Boolean);
    }

    renderPosts(posts) {
      const fragment = document.createDocumentFragment();

      posts.forEach((post, index) => {
        const item = document.createElement('li');
        const button = document.createElement('button');
        const media = document.createElement('span');
        const image = document.createElement('img');
        const copy = document.createElement('span');
        const source = document.createElement('span');
        const action = document.createElement('span');

        item.className = 'arista-instagram__grid-item';
        button.type = 'button';
        button.className = 'arista-instagram__tile';
        button.setAttribute('aria-haspopup', 'dialog');
        button.setAttribute('aria-controls', this.dataset.modalId);
        button.setAttribute('aria-label', `Open ${post.video ? 'video' : 'image'} story from ${post.sourceLabel}`);
        button.dataset.aristaInstagramOpen = '';
        button.dataset.mediaType = post.video ? 'video' : 'image';
        button.dataset.imageSrc = post.image;
        button.dataset.videoSrc = post.video;
        button.dataset.mediaAlt = post.alt;
        button.dataset.storyTitle = 'A little story from Arista';
        button.dataset.storyCaption = post.caption;
        button.dataset.sourceLabel = post.sourceLabel;
        button.dataset.ctaUrl = this.dataset.collectionUrl || '';
        button.dataset.ctaLabel = this.dataset.collectionLabel || 'Shop the edit';
        button.dataset.postId = post.id;

        media.className = 'arista-instagram__tile-media';
        media.setAttribute('aria-hidden', 'true');

        image.src = post.image;
        image.alt = '';
        image.loading = 'lazy';
        image.decoding = 'async';
        image.width = 720;
        image.height = 900;
        image.addEventListener(
          'error',
          () => {
            item.remove();
            if (!this.grid.querySelector('.arista-instagram__grid-item')) this.hideFeed();
          },
          { once: true }
        );
        media.appendChild(image);

        if (post.video) {
          const video = document.createElement('video');
          const playBadge = document.createElement('span');

          video.muted = true;
          video.defaultMuted = true;
          video.playsInline = true;
          video.preload = 'none';
          video.poster = post.image;
          video.dataset.videoSrc = post.video;
          video.dataset.aristaInstagramPreview = '';
          video.tabIndex = -1;

          playBadge.className = 'arista-instagram__play-badge';
          playBadge.innerHTML =
            '<svg class="icon" viewBox="0 0 18 18" aria-hidden="true" focusable="false"><path d="M5.5 3.6v10.8L14 9 5.5 3.6Z" fill="currentColor"/></svg>';

          media.appendChild(video);
          media.appendChild(playBadge);
        }

        copy.className = 'arista-instagram__tile-copy';
        source.className = 'arista-instagram__tile-source';
        source.textContent = post.sourceLabel;
        action.className = 'arista-instagram__tile-action';
        action.textContent = post.video ? 'Watch story' : 'View story';
        copy.appendChild(source);
        copy.appendChild(action);

        button.appendChild(media);
        button.appendChild(copy);
        item.appendChild(button);
        fragment.appendChild(item);

        if (index === posts.length - 1) button.dataset.lastFeedTile = 'true';
      });

      this.grid.replaceChildren(fragment);
      this.registerPreviewMedia(this.grid.querySelectorAll('[data-arista-instagram-preview]'));
    }

    registerPreviewMedia(videos) {
      if (this.reducedMotion || this.saveData || !('IntersectionObserver' in window)) return;

      if (!this.previewObserver) {
        this.previewObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const video = entry.target.querySelector('[data-arista-instagram-preview]');
              if (video) this.previewEntries.set(video, entry.intersectionRatio);
            });
            this.chooseActivePreview();
          },
          { threshold: [0, 0.35, 0.6, 0.85, 1] }
        );
      }

      videos.forEach((video) => {
        const tile = video.closest('[data-arista-instagram-open]');
        if (!tile || this.previewEntries.has(video)) return;
        this.previewEntries.set(video, 0);
        this.previewObserver.observe(tile);
      });
    }

    chooseActivePreview() {
      if (this.modalOpen) {
        this.stopActivePreview();
        return;
      }

      let candidate = null;
      let candidateRatio = 0.59;

      this.previewEntries.forEach((ratio, video) => {
        if (ratio > candidateRatio) {
          candidate = video;
          candidateRatio = ratio;
        }
      });

      if (candidate === this.activePreview) return;
      this.stopActivePreview();
      if (candidate) this.startPreview(candidate);
    }

    async startPreview(video) {
      const source = safeMediaUrl(video.dataset.videoSrc);
      if (!source) return;

      this.activePreview = video;
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;

      if (!video.src) {
        video.src = source;
        video.load();
      }

      const markReady = () => {
        if (this.activePreview === video) video.dataset.ready = 'true';
      };
      const useFallback = () => {
        video.removeAttribute('data-ready');
        if (this.activePreview === video) this.activePreview = null;
      };

      video.addEventListener('loadeddata', markReady, { once: true });
      video.addEventListener('error', useFallback, { once: true });

      try {
        video.currentTime = 0;
        await video.play();
        markReady();
        this.previewFrame = window.requestAnimationFrame(() => this.loopPreview(video));
      } catch (_error) {
        useFallback();
      }
    }

    loopPreview(video) {
      if (this.activePreview !== video || this.modalOpen) return;

      const previewSeconds = this.previewMilliseconds / 1000;
      if (Number.isFinite(video.currentTime) && video.currentTime >= previewSeconds) {
        try {
          video.currentTime = 0;
        } catch (_error) {
          this.stopActivePreview();
          return;
        }
      }

      this.previewFrame = window.requestAnimationFrame(() => this.loopPreview(video));
    }

    stopActivePreview() {
      if (this.previewFrame) window.cancelAnimationFrame(this.previewFrame);
      this.previewFrame = null;

      if (!this.activePreview) return;
      this.activePreview.pause();
      this.activePreview.removeAttribute('data-ready');
      try {
        this.activePreview.currentTime = 0;
      } catch (_error) {
        // The poster remains as the truthful media fallback.
      }
      this.activePreview = null;
    }

    handleSectionClick(event) {
      const trigger = event.target.closest('[data-arista-instagram-open]');
      if (!trigger || !this.contains(trigger)) return;
      this.openModal(trigger);
    }

    openModal(trigger) {
      const imageSource = safeMediaUrl(trigger.dataset.imageSrc);
      const videoSource = safeMediaUrl(trigger.dataset.videoSrc);
      if (!imageSource) return;

      this.modalOpen = true;
      this.modalImageFailed = false;
      this.stopActivePreview();
      this.resetModalMedia();

      this.modalSource.textContent = normaliseText(trigger.dataset.sourceLabel, this.dataset.profileLabel || '@aristajewels.in');
      this.modalTitle.textContent = normaliseText(trigger.dataset.storyTitle, 'A little story from Arista');

      const caption = normaliseText(trigger.dataset.storyCaption);
      this.modalCaption.textContent = caption;
      this.modalCaption.hidden = !caption;

      const ctaUrl = this.safeStoreUrl(trigger.dataset.ctaUrl || this.dataset.collectionUrl);
      if (ctaUrl) {
        this.modalCta.href = ctaUrl;
        this.modalCta.textContent = normaliseText(trigger.dataset.ctaLabel, this.dataset.collectionLabel || 'Shop the edit');
        this.modalCta.hidden = false;
      } else {
        this.modalCta.hidden = true;
        this.modalCta.removeAttribute('href');
      }

      this.modalImage.src = imageSource;
      this.modalImage.alt = normaliseText(trigger.dataset.mediaAlt, 'Arista jewellery story');

      if (trigger.dataset.mediaType === 'video' && videoSource) {
        this.modalVideo.poster = imageSource;
        this.modalVideo.src = videoSource;
        this.modalVideo.setAttribute('aria-label', this.modalImage.alt);
        this.modalVideo.hidden = false;
        this.modalImage.hidden = true;
        this.modalVideo.load();
        this.modalVideo.addEventListener(
          'error',
          () => {
            this.modalVideo.hidden = true;
            if (this.modalImageFailed) {
              this.showModalFallback();
            } else {
              this.modalImage.hidden = false;
            }
          },
          { once: true }
        );
      } else {
        this.modalImage.hidden = false;
      }

      this.shareButton.dataset.shareUrl = this.shareUrl(ctaUrl);
      this.shareButton.dataset.shareTitle = this.modalTitle.textContent;
      this.shareStatus.textContent = '';

      if (typeof this.modal.show === 'function') {
        this.modal.show(trigger);
      } else {
        this.modal.setAttribute('open', '');
        this.modal.querySelector('[role="dialog"]')?.focus();
      }

      if (!this.reducedMotion && !this.saveData && !this.modalVideo.hidden) {
        this.modalVideo.play().catch(() => {});
      }
    }

    handleModalClick(event) {
      const shareButton = event.target.closest('[data-arista-instagram-share]');
      if (shareButton) this.shareStory(shareButton);
    }

    handleModalClosed() {
      if (!this.modalOpen) return;
      this.modalOpen = false;
      this.resetModalMedia();
      if (this.resumePreviewFrame) window.cancelAnimationFrame(this.resumePreviewFrame);
      this.resumePreviewFrame = window.requestAnimationFrame(() => {
        this.resumePreviewFrame = null;
        this.chooseActivePreview();
      });
    }

    handleModalImageError() {
      this.modalImageFailed = true;
      if (!this.modalVideo || this.modalVideo.hidden) this.showModalFallback();
    }

    showModalFallback() {
      if (this.modalVideo) this.modalVideo.hidden = true;
      if (this.modalImage) this.modalImage.hidden = true;
      if (this.modalFallback) this.modalFallback.hidden = false;
    }

    resetModalMedia() {
      if (this.modalVideo) {
        this.modalVideo.pause();
        this.modalVideo.removeAttribute('src');
        this.modalVideo.removeAttribute('poster');
        this.modalVideo.removeAttribute('aria-label');
        this.modalVideo.load();
        this.modalVideo.hidden = true;
      }

      if (this.modalImage) {
        this.modalImage.removeAttribute('src');
        this.modalImage.alt = '';
        this.modalImage.hidden = true;
      }

      if (this.modalFallback) this.modalFallback.hidden = true;
      this.modalImageFailed = false;
    }

    async shareStory(button) {
      const url = this.safeStoreUrl(button.dataset.shareUrl) || window.location.href;
      const title = normaliseText(button.dataset.shareTitle, document.title);

      if (navigator.share) {
        try {
          await navigator.share({ title, url });
          this.shareStatus.textContent = 'Share options opened.';
          return;
        } catch (error) {
          if (error?.name === 'AbortError') return;
        }
      }

      try {
        await navigator.clipboard.writeText(url);
        this.shareStatus.textContent = 'Arista link copied.';
      } catch (_error) {
        const input = document.createElement('textarea');
        input.value = url;
        input.setAttribute('readonly', '');
        input.style.position = 'fixed';
        input.style.opacity = '0';
        document.body.appendChild(input);
        input.select();
        const copied = document.execCommand('copy');
        input.remove();
        this.shareStatus.textContent = copied ? 'Arista link copied.' : 'Copy this page’s address to share it.';
      }
    }

    safeStoreUrl(value) {
      if (!value) return '';

      try {
        const url = new URL(value, window.location.origin);
        return url.origin === window.location.origin ? url.href : '';
      } catch (_error) {
        return '';
      }
    }

    shareUrl(ctaUrl) {
      if (ctaUrl) return ctaUrl;
      const url = new URL(window.location.href);
      url.hash = this.dataset.sectionAnchor || this.id;
      return url.href;
    }

    hideFeed() {
      if (this.grid) {
        this.grid.replaceChildren();
        this.grid.hidden = true;
        this.grid.setAttribute('aria-busy', 'false');
      }

      this.setStatus('Arista stories are temporarily unavailable.');
      if (this.dataset.hasFeaturedStory !== 'true') this.hidden = true;
    }

    setStatus(message) {
      if (this.status) this.status.textContent = message;
    }
  }

  customElements.define('arista-instagram-feed', AristaInstagramFeed);
})();
